import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { resetPassword } from '../lib/api';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle, XCircle, Lock } from 'lucide-react';
import AuthLayout, { authInputClass } from '../components/AuthLayout';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const urlError = searchParams.get('error');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'invalid':
        return 'Invalid or expired reset link. The link may have already been used.';
      case 'expired':
        return 'This reset link has expired. Please request a new password reset.';
      case 'server_error':
        return 'Something went wrong. Please try again later.';
      default:
        return 'Invalid reset link. Please request a new password reset.';
    }
  };

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    } else if (urlError) {
      setError(getErrorMessage(urlError));
    }
  }, [token, urlError]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await resetPassword(token, password);
      setSuccess(true);
      toast.success('Password reset successfully!');

      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password. The link may have expired.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Error state - show if no token or URL has error parameter
  if (error && (!token || urlError)) {
    return (
      <AuthLayout testId="reset-password-page">
        <div className="text-center" data-testid="reset-password-error">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <XCircle size={32} className="text-red-500" />
          </div>
          <h1 className="text-xl font-extrabold text-slate-900 mb-2">Invalid or Expired Link</h1>
          <p className="text-sm text-slate-600 mb-5">{error}</p>
          <button
            onClick={() => navigate('/forgot-password')}
            className="w-full h-12 rounded-2xl font-bold text-base text-white flex items-center justify-center gap-2 bg-gradient-to-r from-[#57B22E] to-[#3E9A1C] shadow-lg shadow-green-600/30 hover:from-[#4CA327] hover:to-[#358916] active:scale-[0.99] transition-all"
          >
            Request New Link
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Success state
  if (success) {
    return (
      <AuthLayout testId="reset-password-page">
        <div className="text-center" data-testid="reset-password-success">
          <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-emerald-500" />
          </div>
          <h1 className="text-xl font-extrabold text-slate-900 mb-2">Password Reset!</h1>
          <p className="text-sm text-slate-600 mb-5">Your password has been reset successfully. Redirecting you to login...</p>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
          </div>
        </div>
      </AuthLayout>
    );
  }

  const matchOk = confirmPassword && password === confirmPassword && password.length >= 6;

  return (
    <AuthLayout testId="reset-password-page">
      <div className="text-center mb-4">
        <h1 className="text-xl font-extrabold text-slate-900">Reset password</h1>
        <p className="text-sm text-slate-500 mt-0.5">Create a new secure password</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
        <div>
          <label className="block text-sm font-bold text-slate-800 mb-1.5">New Password</label>
          <div className="relative">
            <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a secure password"
              className={`${authInputClass} pl-9 pr-10`}
              required
              minLength={6}
              data-testid="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 inset-y-0 flex items-center text-slate-400 hover:text-slate-600 z-10"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-1.5">Must be at least 6 characters</p>
        </div>

        <div>
          <label className="block text-sm font-bold text-slate-800 mb-1.5">Confirm New Password</label>
          <div className="relative">
            <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              className={`${authInputClass} pl-9 pr-3`}
              required
              minLength={6}
              data-testid="confirm-password-input"
            />
          </div>
          {confirmPassword && password !== confirmPassword && (
            <p className="text-xs text-red-500 mt-1.5">Passwords do not match</p>
          )}
          {matchOk && (
            <p className="text-xs text-emerald-500 mt-1.5 flex items-center gap-1">
              <CheckCircle size={14} />
              Passwords match
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || password.length < 6 || password !== confirmPassword}
          className="w-full h-12 rounded-2xl font-bold text-base text-white flex items-center justify-center gap-2 bg-gradient-to-r from-[#57B22E] to-[#3E9A1C] shadow-lg shadow-green-600/30 hover:from-[#4CA327] hover:to-[#358916] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="reset-button"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Resetting Password...
            </span>
          ) : (
            'Reset Password'
          )}
        </button>
      </form>

      <div className="mt-4 text-center">
        <p className="text-sm text-slate-500">
          Remember your password?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-blue-600 hover:text-blue-700 font-semibold"
          >
            Sign in
          </button>
        </p>
      </div>
    </AuthLayout>
  );
}
